/**
 * It's a generated file, preferable to extend this in external file
 */
import * as yup from 'common/yup';
import {CONTEXT_SEND_PARAMETER} from 'common/yup/constants';
import {idString} from 'common/yup/customs/idString';
import {passphrase} from 'common/yup/customs/passphrase';
import {isEmpty} from 'lodash';
import {nullableDate} from '../common/yup';
import i18n from '../i18n';
import {DailyWindow} from './DailyWindow';
import {DayOfWeek} from './DayOfWeek';
import {enums} from './enums';
import {HolidayBehavior} from './HolidayBehavior';
import {HolidayCalendar} from './HolidayCalendar';

const ScheduleCore = yup.object({
   disabled: yup.boolean(),
   doPreProcessing: yup.bool().default(true),
   highPriority: yup.boolean(),
   allowApi: yup.boolean(),
   allowDaisyChained: yup.boolean(),
   allowScriptingAgent: yup.boolean(),
   useFileSystemMonitor: yup.boolean(),
   timeScheduled: yup.boolean(),
   scriptingAgentPassword: passphrase(),
   fsmTimeout: yup.number().integer(),
   fsmIncludeModified: yup.boolean().default(false),
   fsmRecursive: yup.bool(),
   recurrenceType: yup.string().oneOf(enums.recurrenceType.keys),
   recurrence: yup.number().integer().min(1),
   startTime: yup.date(),
   expiration: nullableDate(),
   excludedDays: yup.array().of(DayOfWeek),
   calendarId: idString(),
   calendar: HolidayCalendar.nullable(),
   holidayBehavior: HolidayBehavior,
   retryInterval: yup.number().integer(),
   numberRetries: yup.number().integer(),
   notifyOnRetry: yup.boolean(),
   dailyWindow: DailyWindow,
   months: yup.array().of(yup.string().oneOf(enums.months.keys)),
   daysOfMonth: yup.array().of(yup.string()),
   businessDaysOfMonth: yup.array().of(yup.string()),
   weeksOfMonth: yup.array().of(yup.string()),
   daysOfWeek: yup.array().of(DayOfWeek),
   dayOfWeek: DayOfWeek,
});

const ScheduleToSend = ScheduleCore.clone()
   .transform(function (value, originalValue) {
      // console.debug('LocalTransportSchema transform init ', value, originalValue, this);
      if (!value._trigger) {
         return value;
      }

      value = {
         ...originalValue,
      };
      delete value._trigger;

      switch (originalValue._trigger) {
         case ScheduleTrigger.FILE_MONITOR:
            value.useFileSystemMonitor = true;
            value.timeScheduled = false;
            break;
         case ScheduleTrigger.SCHEDULE:
            value.useFileSystemMonitor = false;
            value.timeScheduled = true;
            break;
         default:
            value.useFileSystemMonitor = false;
            value.timeScheduled = false;
            break;
      }

      return value;
   })
   .transform(function (value, originalValue) {
      // Empty businessDaysOfMonth if no calendar selected
      if (!value.calendar || !value.calendar.id) {
         value.businessDaysOfMonth = [];
      }
      return value;
   })
   .transform(function (value, originalValue) {
      // Empty businessDaysOfMonth if no calendar selected
      if (value._everyMonthsRadio === everyMonthsRadios.RECURRENCE) {
         value.months = [];
      }
      return value;
   })
   .transform(function (value, originalValue) {
      // Empty businessDaysOfMonth if no calendar selected
      if (value._pollingStartTimeRadio === pollingStartTimeRadios.START_TIME) {
         // value.dailyWindow.from = null;
         value.dailyWindow = DailyWindow.cast({
            ...value.dailyWindow,
            from: null,
         });
      }
      return value;
   });

export type ISchedule = yup.InferType<typeof ScheduleCore>;

export const ScheduleTrigger = {
   FILE_MONITOR: 'File Monitoring',
   NONE: '<NONE>',
   SCHEDULE: 'Schedule',
};

export const pollingStartTimeRadios = {
   START_TIME: 'START_TIME',
   DAILY_WINDOW: 'DAILY_WINDOW',
};

export const everyMonthsRadios = {
   RECURRENCE: 'RECURRENCE',
   CHOOSE: 'CHOOSE',
};

const ScheduleWithHelpFields = ScheduleCore.shape({
   _trigger: yup.string(),
   _pollingStartTimeRadio: yup
      .string()
      .oneOf([pollingStartTimeRadios.START_TIME, pollingStartTimeRadios.DAILY_WINDOW])
      .default(pollingStartTimeRadios.START_TIME),
   _everyMonthsRadio: yup.string().default(everyMonthsRadios.RECURRENCE),
})
   .transform(function (value, originalValue) {
      // console.debug('LocalTransportSchema transform init ', value, originalValue, this);
      if (value._trigger) {
         return value;
      }

      if (value.useFileSystemMonitor) {
         value._trigger = ScheduleTrigger.FILE_MONITOR;
      } else if (value.timeScheduled) {
         value._trigger = ScheduleTrigger.SCHEDULE;
      } else {
         value._trigger = ScheduleTrigger.NONE;
      }

      return value;
   })
   .transform(function (value, originalValue) {
      // console.debug('LocalTransportSchema transform init ', value, originalValue, this);
      if (value._pollingStartTimeRadio) {
         return value;
      }

      if (value.dailyWindow.from) {
         value._pollingStartTimeRadio = pollingStartTimeRadios.DAILY_WINDOW;
      } else {
         value._pollingStartTimeRadio = pollingStartTimeRadios.START_TIME;
      }

      return value;
   })
   .transform(function (value, originalValue) {
      // console.debug('LocalTransportSchema transform init ', value, originalValue, this);
      if (value._everyMonthsRadio) {
         return value;
      }

      if (!isEmpty(value.months)) {
         value._everyMonthsRadio = everyMonthsRadios.CHOOSE;
      } else {
         value._everyMonthsRadio = everyMonthsRadios.RECURRENCE;
      }

      return value;
   });
export type IScheduleWithHelpFields = yup.InferType<typeof ScheduleWithHelpFields>;

export const Schedule = ScheduleCore.when(CONTEXT_SEND_PARAMETER, {
   is: true,
   then: ScheduleToSend,
   otherwise: ScheduleWithHelpFields,
}) as typeof ScheduleWithHelpFields;
